import { useMemo, useState } from "react";
import {
  Mail, MessageCircle, Calendar, ShieldCheck, IndianRupee, Sparkles,
  ArrowRight, Brain,
} from "lucide-react";
import { useStudioLeadsLive } from "../../lib/useStudioLeadsLive";
import type { DerivedLead } from "../../lib/studioFromGraph";
import { cls } from "../../lib/cls";

/* TrackerAgenticStrip — every lead-stage tile is anchored to a real
 * lead on the studio's pipeline. Click a stage → the strip shows
 * the next agent action for THAT lead, derived live from the
 * project graph (useStudioLeadsLive). No literal copy. */

type StageId = DerivedLead["stage"];

const STAGE_ORDER: ReadonlyArray<StageId> = ["new", "qualified", "proposal", "won", "lost"];

const STAGE_LABEL: Record<StageId, string> = {
  new:       "New",
  qualified: "Qualified",
  proposal:  "Proposal",
  won:       "Won",
  lost:      "Lost",
};

const STAGE_TONE: Record<StageId, string> = {
  new:       "bg-cream-dark border-border text-ink-700",
  qualified: "bg-sky-50    border-sky-200  text-sky-800",
  proposal:  "bg-amber-50  border-amber-200 text-amber-800",
  won:       "bg-fern/10   border-fern/30   text-fern",
  lost:      "bg-foyr/10   border-foyr/30   text-foyr-deep",
};

interface AgentAction {
  icon: JSX.Element;
  label: string;
  detail: string;
}

function actionForLead(lead: DerivedLead): AgentAction {
  // Lead.next_action.label already encodes the agent's recommendation
  // (computed from age + stage + tier in studioFromGraph). Wrap it
  // with the right icon + a one-line explanation of *why*.
  const label = lead.next_action.label;
  const detail = `${lead.budget_tier} tier · ${lead.age_days} d in ${lead.stage} · routed to ${lead.next_action.agent}`;
  const icon =
    /book|call|visit/i.test(label)            ? <Calendar className="w-3 h-3" /> :
    /draft|email|note/i.test(label)            ? <Mail className="w-3 h-3" /> :
    /approv|sign/i.test(label)                 ? <ShieldCheck className="w-3 h-3" /> :
    /estimat|quot|budget/i.test(label)          ? <IndianRupee className="w-3 h-3" /> :
    /follow|nudge|reach/i.test(label)           ? <MessageCircle className="w-3 h-3" /> :
                                                 <Sparkles className="w-3 h-3" />;
  return { icon, label, detail };
}

export function TrackerAgenticStrip(): JSX.Element {
  const { leads, source } = useStudioLeadsLive();
  const [stage, setStage] = useState<StageId>("qualified");

  const byStage = useMemo(() => {
    const m: Record<StageId, DerivedLead[]> = { new: [], qualified: [], proposal: [], won: [], lost: [] };
    for (const l of leads) m[l.stage].push(l);
    return m;
  }, [leads]);

  const stageLeads = byStage[stage];
  const project = stageLeads[0];

  return (
    <section className="panel p-4 sm:p-5 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-ink-950 text-cream grid place-items-center shrink-0">
            <Brain className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0">
            <div className="eyebrow">What the agent does at each stage</div>
            <div className="text-[14.5px] font-semibold text-ink-950 leading-tight">
              {leads.length} leads across {STAGE_ORDER.length} stages — pick one to see the next agent action.
            </div>
          </div>
        </div>
        <span className={cls(
          "pill text-[9.5px] border shrink-0",
          source === "agent" ? "bg-fern/10 text-fern border-fern/30" : "bg-cream-dark text-ink-500 border-border",
        )}>
          {source === "agent" ? "live · agent" : "from your projects"}
        </span>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {STAGE_ORDER.map((s) => {
          const count = byStage[s].length;
          const active = stage === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStage(s)}
              className={cls(
                "rounded-xl border px-3 py-2.5 text-left transition-all",
                active ? STAGE_TONE[s] + " shadow-[0_2px_10px_-4px_rgba(0,0,0,0.18)] ring-1 ring-foyr/30" : "bg-cream border-border text-ink-700 hover:border-foyr/30",
              )}
            >
              <div className="text-[9.5px] uppercase tracking-wider font-mono opacity-80">{STAGE_LABEL[s]}</div>
              <div className="display-3 tabular leading-none mt-1">{count}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 rounded-xl border border-border bg-cream/70 px-3.5 py-3">
        {stageLeads.length === 0 ? (
          <div className="text-[12px] text-ink-500 italic">
            No leads in <span className="font-medium text-ink-700">{STAGE_LABEL[stage]}</span> right now.
            The agent will pick this strip up the moment a lead lands here.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[10.5px] uppercase tracking-wider font-mono text-ink-500">
              Showing 1 of {stageLeads.length} · {STAGE_LABEL[stage]}
            </div>
            {project ? <LeadRow lead={project} action={actionForLead(project)} /> : null}
          </div>
        )}
      </div>
    </section>
  );
}

function LeadRow({ lead, action }: { lead: DerivedLead; action: AgentAction }): JSX.Element {
  // Choose a real, working handler for the agent action. We never
  // ship a CTA that does nothing.
  const handler = pickActionHandler(lead, action);
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-ink-950 leading-tight">{lead.name}</div>
        <div className="text-[11px] text-ink-500 leading-tight mt-0.5">
          {lead.city} · {lead.budget_tier} · {lead.age_days} d in stage
        </div>
      </div>
      <button
        type="button"
        onClick={handler.onClick}
        title={handler.title}
        className="rounded-lg border border-foyr/30 bg-foyr/5 px-2.5 py-1.5 max-w-[420px] text-left hover:bg-foyr/10 hover:border-foyr/50 transition cursor-pointer"
      >
        <div className="flex items-center gap-1.5 text-foyr-deep text-[11.5px] font-semibold leading-tight">
          {action.icon} {action.label}
          <ArrowRight className="w-3 h-3 ml-1 opacity-60" />
        </div>
        <div className="text-[10.5px] text-ink-700 italic mt-0.5">{action.detail}</div>
      </button>
    </div>
  );
}

/** Map an agent action label → a concrete browser action. Same
 *  semantics the cockpit's `inferSourceApp` derives — keeps every
 *  CTA wired to a real next step. */
function pickActionHandler(lead: DerivedLead, action: AgentAction): { onClick: () => void; title: string } {
  const label = action.label;
  const isMail = /draft|email|note|concept board|send/i.test(label);
  const isCall = /call|book|visit/i.test(label);
  const subject = encodeURIComponent(`${lead.name} · ${action.label}`);
  const body = encodeURIComponent(
    `Hi ${lead.name},\n\nFollowing up on your enquiry from ${lead.city} · ${lead.budget_tier} tier.\n\nNext step: ${action.label}.\n\n— sent from the FoyrMind copilot`,
  );
  if (isMail) {
    const leadEmail: string | null = lead.email && lead.email !== "—" ? lead.email : null;
    const to = leadEmail ?? "";
    return {
      title: `Open email draft to ${to || "client"}`,
      onClick: () => { window.location.href = `mailto:${to}?subject=${subject}&body=${body}`; },
    };
  }
  if (isCall) {
    return {
      title: "Open the project so you can schedule the visit",
      onClick: () => {
        try { window.localStorage.setItem("foyrmind.activeProjectId", lead.project_id ?? ""); } catch { /* ignore */ }
        try { window.dispatchEvent(new Event("foyrmind:projectChanged")); } catch { /* ignore */ }
        window.location.href = "/";
      },
    };
  }
  // Default: switch the cockpit to this project so the designer
  // can take whatever next step the action implies.
  return {
    title: "Open this lead in the cockpit",
    onClick: () => {
      try { window.localStorage.setItem("foyrmind.activeProjectId", lead.project_id ?? ""); } catch { /* ignore */ }
      try { window.dispatchEvent(new Event("foyrmind:projectChanged")); } catch { /* ignore */ }
      window.location.href = "/";
    },
  };
}
